import { useCallback, useRef, useState } from 'react'
import Head from 'next/head'

import BodyWrapper from '@components/atoms/Body'
import CourseWrapper from '@components/atoms/CourseWrapper'
import GroupedCourses from '@components/atoms/GroupedCourses'
import Layout from '@components/organisms/Layouts/WithoutSidebar'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { ShortCourse } from '@type/course'
import CloseIcon from '@public/static/icons/close-icon-dark'

// Hooks
import { useRouter } from 'next/router'
import { useAuth } from '@contexts/Auth'
import { useTranslation } from 'react-i18next'
import useGetAllCourses from '@services/react-query/hooks/courses/queries/useGetAllCourses'

import { getCourses } from '@services/api/course/getCourses'
// Loaders
import { Skeleton } from '@nextui-org/react-latest'
import VerticalCourseSkeleton from '@components/molecules/VerticalCourseSkeleton'
import GroupedButtonsLoading from '@components/organisms/Skeletons/GroupedButtonsLoading'

// Dynamic Components
import dynamic from 'next/dynamic'
const StickyFooter = dynamic(
  () => import('@components/pages/home/StickyFooter')
)
const BackToTop = dynamic(() => import('@components/molecules/BackToTop'), {
  ssr: false,
})
const CartModal = dynamic(() => import('@components/organisms/CartModal'))
import VerticalCourse from '@components/molecules/VerticalCourse'
import { getEmptyFilters } from '@helpers/courses/emptyFilters'
import { GetServerSidePropsContext } from 'next'
import getCategories from '@services/api/category/getCategories'
import { Category } from '@type/main'
import { AxiosResponse } from 'axios'
import { ParsedUrlQuery } from 'querystring'
// import { performance } from 'perf_hooks'
// const VerticalCourse = dynamic(
//   () => import('@components/molecules/VerticalCourse'),
//   {
//     loading: () => <VerticalCourseSkeleton />,
//   }
// )
const MyCoursesSection = dynamic(
  () => import('@components/molecules/MyCoursesSection')
)
const GroupedButtons = dynamic(
  () => import('@components/molecules/GroupedButtons'),
  {
    loading: () => (
      <div className="flex justify-between w-full min-w-[78rem] sm:h-[60px] items-center mt-2 sm:ml-0 sm:py-2 mb-1 ml-[7.6px]">
        {GroupedButtonsLoading}
      </div>
    ),
  }
)

const SubCategoriesBlock = dynamic(
  () => import('@components/pages/home/SubCategoriesBlock'),
  {
    loading: () => (
      <div className="flex justify-between w-full min-w-[78rem] sm:h-[60px] items-center mt-2 sm:ml-0 sm:py-2 mb-1 ml-[7.6px]">
        {GroupedButtonsLoading}
      </div>
    ),
  }
)
// const Layout = dynamic(
//   () => import('@components/organisms/Layouts/WithoutSidebar'),
//   {
//     loading: () => <VerticalCourseSkeleton />,
//   }
// )

const localeNamespaces = [
  'common',
  'home',
  'authentication',
  'footer',
  'courses',
  'signUp',
  'home',
  'wishlist',
  'cartModal',
  'wishlistDropdown',
  'errors',
]

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const { locale = 'en', res, query } = context

  let courses: AxiosResponse | null = null
  let categories: Category[] = []
  let translations
  // const start = performance.now()
  try {
    const { filters, searchQuery, sort } = getParams(query)

    const [coursesResult, categoriesResult, translationsResult] =
      await Promise.all([
        getCourses(searchQuery, filters, sort || { rating: -1 }, 1, 20),
        getCategories(),
        serverSideTranslations(locale, localeNamespaces),
      ])

    courses = coursesResult
    categories = categoriesResult
    translations = translationsResult
  } catch (error) {
    translations = await serverSideTranslations(locale, localeNamespaces)
  }

  // const duration = performance.now() - start
  // console.log(`getServerSideProps await time: ${duration.toFixed(2)}ms`)

  res.setHeader(
    'Cache-Control',
    'public, s-maxage=3600, stale-while-revalidate=7200'
  )

  return {
    props: {
      ...translations,
      courses: courses?.data || [],
      total: parseInt(courses?.headers?.['x-total'] || '0', 10),
      categories,
    },
  }
}

const initialSort = { rating: -1 }

interface Props {
  courses: ShortCourse[]
  total: number
}

const getParams = (query: ParsedUrlQuery) => {
  const searchQuery = decodeURIComponent(query?.search?.toString() || '')
  const qcategory = query?.category
    ? JSON.parse(query?.category?.toString())
    : undefined
  const subCategory = query?.subCategory
    ? JSON.parse(query.subCategory?.toString())
    : undefined
  const urlFilters = query?.filters
  const filters = urlFilters
    ? JSON.parse((urlFilters as string) || '{}')
    : getEmptyFilters()
  if (qcategory) {
    filters.category = qcategory
  }
  if (subCategory) {
    filters.subCategory = subCategory
  }
  const sort = !query?.sort
    ? initialSort
    : JSON.parse((query?.sort as string) || '{}')

  return {
    searchQuery,
    filters,
    sort,
    urlFilters,
  }
}

const Home = ({ courses: initialCourses, total: initialTotal }: Props) => {
  const [category, setCategory] = useState<string>('')
  const [showCheckoutModal, setShowCheckoutModal] = useState(false)

  const observer = useRef<IntersectionObserver>(null)
  const router = useRouter()
  const { sort, searchQuery, filters, urlFilters } = getParams(router.query)
  const {
    data: results,
    isLoading = true,
    total,
    fetchNextPage,
    isFetchingNextPage,
  } = useGetAllCourses({
    query: searchQuery,
    filters: filters || {},
    sort,
    limit: 20,
    initialCourses,
    initialTotal,
  })
  const { isAuthenticated, user } = useAuth()
  const { t, i18n } = useTranslation()

  const callback = (enteries) => {
    if (enteries[0].isIntersecting && total > 20) {
      fetchNextPage()
    }
  }

  const options = {
    root: null,
    rootMargin: '0px',
    threshold: 0,
  }

  const containerRef = useCallback(
    (node: HTMLDivElement) => {
      if (isLoading) return
      if (observer.current) observer.current?.disconnect()

      observer.current = new IntersectionObserver(callback, options)

      if (node) observer.current.observe(node)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoading, total]
  )

  const resetCategories = () => {
    setCategory('all')
  }

  const resetFilters = () => {
    router.push(router.pathname)
  }

  const loadingComponent = [...Array(10)].map((_, i) => (
    <VerticalCourseSkeleton key={i} />
  ))

  const getTopCoursesComponents = (
    results: ShortCourse[],
    containerRef: (node: HTMLDivElement) => void
  ) => {
    return results.map((course, i) => (
      <VerticalCourse
        key={course._id}
        course={course}
        containerRef={containerRef}
        index={i}
        resultsLength={results.length}
        setShowCheckoutModal={setShowCheckoutModal}
      />
    ))
  }

  return (
    <div>
      <Head>
        <title>Courses - Edugram.io</title>
        <meta name="og:title" content="Edugram.io" />
        <meta name="description" content={t('description', { ns: 'home' })} />
        <meta
          name="og:description"
          content={t('description', { ns: 'home' })}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={'Edugram.io Courses'} />
        <meta
          name="twitter:description"
          content={t('description', { ns: 'home' })}
        />
        <meta property="og:image" content="/images/edugram.png" />
        <meta property="og:image:secure_url" content="/images/edugram.png" />
        <meta property="og:type" content="video_lecture" />
        <meta property="og:site_name" content="Edugram.io" />
        <meta property="og:locale" content={i18n.language} />
        <meta name="twitter:domain" content="www.edugram.io" />
        <meta
          name="keywords"
          content={
            'Edugram.io, education, courses, learning, resources, skills, online learning, personalized learning, career coaching, personal development, skill development, professional development'
          }
        />
      </Head>
      <Layout
        onLogoClick={resetCategories}
        category={category}
        setCategory={setCategory}
        showCategories={true}
      >
        <BodyWrapper>
          <CourseWrapper>
            <div className="flex justify-between w-full flex-wrap items-center sm:mt-4 relative">
              {Object.keys(router.query?.filters || {}).length > 0 ? (
                isLoading ? (
                  <Skeleton className="w-36 h-[2.6rem] rounded-[20px]" />
                ) : (
                  <div className="border border-uguPurple rounded-[20px] items-center min-w-max flex text-base py-[7px] px-[16px] mt-[1rem] max-w-max justify-between font-regular tracking-[0rem] text-uguPurple">
                    {total} {t('home.filteredResults', { ns: 'common' })}
                    <div
                      className="ml-4 cursor-pointer max-w-max"
                      onClick={resetFilters}
                    >
                      <CloseIcon height="1rem" width="16px" />
                    </div>
                  </div>
                )
              ) : filters.category.length === 0 ? (
                searchQuery.trim().length == 0 ? (
                  <div className="w-full hidden sm:inline">
                    <GroupedButtons
                      category={category}
                      setCategory={setCategory}
                    />
                  </div>
                ) : isLoading ? (
                  <Skeleton className="w-36 h-[2.6rem] rounded-[20px] mt-3 sm:mt-0" />
                ) : (
                  <span className="mt-3 sm:mt-0">
                    <div className="border border-uguPurple rounded-3xl text-base flex items-center mb-4 break-all py-[7px] px-4 max-w-full sm:max-w-xl md:max-w-2xl lg:max-w-4xl">
                      <div className="font-regular tracking-[0rem] text-uguPurple break-words line-clamp-1">
                        {isLoading ? '' : total}{' '}
                        <span>{t('home.resultsFor', { ns: 'common' })}</span>{' '}
                        &quot;
                        <span className="font-semiBold tracking-[0rem] break-words">
                          {searchQuery.trim()}
                        </span>
                        &quot;
                      </div>
                      <div
                        onClick={resetFilters}
                        className="ml-2 cursor-pointer"
                      >
                        <CloseIcon height="1rem" width="1rem" />
                      </div>
                    </div>
                  </span>
                )
              ) : (
                <>
                  <SubCategoriesBlock filters={filters} />
                </>
              )}
            </div>
            <GroupedCourses>
              {isLoading ? (
                loadingComponent
              ) : results?.length > 0 ? (
                <>
                  {getTopCoursesComponents(results, containerRef)}
                  {isFetchingNextPage ? loadingComponent : <></>}
                </>
              ) : (
                !isLoading && (
                  <span
                    style={{
                      gridColumnStart: 1,
                      gridColumnEnd: -1,
                    }}
                    className="text-lg text-uguDarkGrey m-auto my-[5rem] font-semiBold"
                  >
                    {t('noCoursesFound', { ns: 'home' })}
                  </span>
                )
              )}
              {Object.keys(urlFilters || {}).length === 0 &&
                filters.category?.length === 0 &&
                (category === 'all' || category === '') &&
                isAuthenticated &&
                !isLoading &&
                !router.query?.search &&
                (user?.purchased_courses?.length || 0) > 0 && (
                  <MyCoursesSection filters={filters} max={4} />
                )}
            </GroupedCourses>
          </CourseWrapper>
          <div id="moreCoursesTrigger"></div>
          <StickyFooter />
          {showCheckoutModal ? (
            <CartModal
              isOpen={showCheckoutModal}
              onClose={() => {
                setShowCheckoutModal(false)
              }}
            />
          ) : (
            <></>
          )}
        </BodyWrapper>
        <BackToTop />
      </Layout>
    </div>
  )
}

export default Home
